import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import brainTreeApplePayClient, { ApplePayInstance } from 'braintree-web/apple-pay';
import { useSelector } from 'react-redux';
import classNames from 'classnames';

import { brainTreeApi } from '../../../../../store/braintree';
import { RootState } from '../../../../../store';
import paypal from '../../../../../store/paypal';
import i18n from '../../../../../utils/translations/i18n';
import { currencyFormatter } from '../../../../../utils/currency';
import { logSentry } from '../../../../../utils/sentry';
import { addPCCFlag } from '../../../../shopping-bag/ga-module';
import { CHANNEL_TYPE, EXPRESS_CHECKOUT_COOKIE_NAMES, PAYMENT_METHODS, PAYMENT_METHOD_CAMEL_CASE } from '../../../../../enums/checkoutPayments';
import styles from './apple-pay.module.scss';
import { dispatchGaAndFacebookEvent } from '../../../../../utils/analytics';
import { setDisabledItem, setPaymentMethod, setShowWarningOnCartPage } from '../../../../../store/application';
import applePay from '../../../../../store/applePay';
import { cartApi } from '../../../../../store/cart';
import { remove as removeCookie} from '../../../../../utils/cookies';
import { LOCALE_UPDATED_SALE_LINK } from '../../../../../utils/payment/constants';
import { config } from '../../../../../utils/config';

type ApplePayInstanceType = ApplePayInstance | null;

export const ApplePay = (props: any) => {
    const { isExpressCheckout, cart, totalCartAmount, show, dispatch, redirectToOrderConfirmation, setShowPaymentError, startLoading, stopLoading, icon: Icon } = props;
    const { t: translate } = i18n;
    const navigate = useNavigate();
    const { applicationState: { user, disabledItem, showWarning } } = useSelector((state: RootState) => state);
    const [triggerMiniCartQuery] = cartApi.endpoints.getLatestMiniCart.useLazyQuery();
    const [applePayInstance, setApplePayInstance] = useState<ApplePayInstanceType>(null);
    const applePayButtonRef = useRef<HTMLButtonElement>(null);

    useEffect(() => {
        if (applePayButtonRef.current) {
            applePayButtonRef.current.style.appearance = '-apple-pay-button';
            applePayButtonRef.current.style.setProperty('-apple-pay-button-style', 'white', 'important');
        }
    }, []);

    useEffect(() => {
        const initialize = async () => {
            try {
                dispatch(startLoading());
                removeCookie(EXPRESS_CHECKOUT_COOKIE_NAMES.APPLE_PAY_EXPRESS_CHECKOUT);
                const { data: { token } } = await dispatch(brainTreeApi.endpoints.getCardAuthToken.initiate({ cartId: user.cartId, paymentMethod: PAYMENT_METHOD_CAMEL_CASE.CREDIT_CARD }));
                const braintreeClientInstance = await paypal.createClientInstance(token);
                const applePayInstance = await brainTreeApplePayClient.create({ client: braintreeClientInstance });
                setApplePayInstance(applePayInstance);
                dispatch(stopLoading());
            } catch (error) {
                dispatch(stopLoading());
                logSentry({ page: 'checkoutPayment', feature: 'getPaypalAuthToken', operation: 'getPaypalAuthToken', errorType: 'ajaxError', err: JSON.stringify(error), func: 'braintree-applePay --> initialize', api: null, errMsg: 'Error: while initializing Braintree' });
            }
        };
        initialize();
    }, []);

    const prepareCheckoutRequestData = (cartId, nonce) => ({
        id: cartId,
        idType: 'cartId',
        channel: 'ABC',
        nonce,
        transactionType: 'Charge', // for testing purposes
    });

    const tokenizeApplePay = async (event, session) => {
        applePayInstance.tokenize({ token: event.payment.token }, async (tokenizeErr, payload) => {
            dispatch(startLoading());
            try {
                if (tokenizeErr) throw new Error(tokenizeErr);
                const requestedData = prepareCheckoutRequestData(cart.id, payload.nonce);
                const resp = await paypal.submitNonceNew(dispatch, requestedData, redirectToOrderConfirmation, setShowPaymentError, 'Apple Pay', cart, PAYMENT_METHODS.BRAINTREE_APPLE_PAY);
                session.completePayment(resp?.isSucessfull ? (window as any).ApplePaySession.STATUS_SUCCESS : (window as any).ApplePaySession.STATUS_FAILURE);
            } catch (error) {
                setShowPaymentError(true);
                session.completePayment((window as any).ApplePaySession.STATUS_FAILURE);
                logSentry({ page: 'checkoutPayment', feature: 'applePay', operation: 'onpaymentauthorized', errorType: 'ajaxError', err: JSON.stringify(error), func: 'apple-pay --> onpaymentauthorized', api: null, errMsg: 'Error: while tokenizing the apple pay' });
            } finally {
                dispatch(stopLoading());
            }
        });
    };

    const tokenizeExpressCheckoutApplePay = async (event, session) => {
        const { customerData, shippingAddressData, billingAddressData } = applePay.createDataFromApplePayDetails(event.payment);
        applePayInstance.tokenize({ token: event.payment.token }, async (tokenizeErr, payload) => {
            dispatch(startLoading());
            try {
                if (tokenizeErr) throw new Error(tokenizeErr);
                if(disabledItem) {
                    dispatch(setDisabledItem(false));
                }
                if(showWarning){
                    dispatch(setShowWarningOnCartPage(false));
                }
                const { nonce } = payload;
                const cartId = cart.id;
                const shippingType = cart.shippingType ?? '';
                if (shippingAddressData.country !== (window as any).locale_details?.country_code) 
                    throw new Error('Country mismatch while tokenizing apple pay express flow');
                
                const expressCheckoutCookieName = EXPRESS_CHECKOUT_COOKIE_NAMES.APPLE_PAY_EXPRESS_CHECKOUT;
                if (user.isLoggedIn === 'true') {
                    await paypal.updateCustomerAndCartAddressLoggedIn({shippingAddressData,billingAddressData,nonce, expressCheckoutCookieName, user, cartId, shippingType, dispatch, navigate, triggerMiniCartQuery, channelType: CHANNEL_TYPE.APPLE_PAY_EXPRESS});
                } else {
                    await paypal.updateCustomerAndCartAddressGuest({customerData,shippingAddressData,billingAddressData,nonce, expressCheckoutCookieName, cartId, user, shippingType, dispatch, setShowPaymentError, navigate, triggerMiniCartQuery});
                }
                session.completePayment((window as any).ApplePaySession.STATUS_SUCCESS);
            } catch (error) {
                setShowPaymentError(true);
                dispatch(stopLoading());
                session.completePayment((window as any).ApplePaySession.STATUS_FAILURE);
                logSentry({ page: 'checkoutPayment', feature: 'applePay', operation: 'onpaymentauthorized', errorType: 'ajaxError', err: JSON.stringify(error), func: 'apple-pay --> onpaymentauthorized', api: null, errMsg: 'Error: while tokenizing the apple pay express checkout' });
            }
        });
    };

    const handleApplePay = async event => {
        event.preventDefault();
        
        dispatch(setPaymentMethod('apple_pay_braintree'));
        const _metaData = {
            'event': "payment_method",
            'payment_type': isExpressCheckout ? 'Apple Pay Checkout' : 'Apple Pay',
            'step_name': 'continue_button',
            'step_no': 1,
            ...addPCCFlag(cart?.hasPCCItem)
        }
        dispatchGaAndFacebookEvent(_metaData);
        
        try {
            const paymentRequest = applePayInstance?.createPaymentRequest({
                total: {
                    label: 'Pens.com',
                    amount: totalCartAmount
                },
                requiredBillingContactFields: ['postalAddress'],
                ...(isExpressCheckout ? {requiredShippingContactFields: ['postalAddress', 'name', 'phone', 'email']} : {}),
            });

            const session = new (window as any).ApplePaySession(3, paymentRequest);

            session.onvalidatemerchant = function (event) {
                applePayInstance.performValidation({ 
                    validationURL: event.validationURL,
                    displayName: 'Pens.com'
                }, (err, merchantSession) => {
                    if (err) {
                        setShowPaymentError(true);
                        logSentry({
                            page: 'checkoutPayment',
                            feature: 'applePay',
                            operation: 'onvalidatemerchant',
                            errorType: 'ajaxError',
                            err: JSON.stringify(err),
                            func: 'apple-pay --> onvalidatemerchant',
                            api: null,
                            errMsg: 'Error: while tokenizing the apple pay',
                        });
                        return;
                    }
                    session.completeMerchantValidation(merchantSession);
                });
            };
            session.oncancel = function (event) {
                var _metaData = {
                    'event': "payment_method",
                    'payment_type': isExpressCheckout ? 'Apple Pay Checkout' : 'Apple Pay',
                    'step_name': 'cancel_payment',
                    'step_no': 0,
                    ...addPCCFlag(cart?.hasPCCItem)
                }
                dispatchGaAndFacebookEvent(_metaData);
            }
            session.onpaymentauthorized = async event => {
                isExpressCheckout ? await tokenizeExpressCheckoutApplePay(event, session) : await tokenizeApplePay(event, session);
            };

            await session.begin();

        } catch (error) {
            setShowPaymentError(true);
            logSentry({ page: 'checkoutPayment', feature: 'applePay', operation: 'handleApplePay', errorType: 'ajaxError', err: JSON.stringify(error), func: 'apple-pay --> handleApplePay', api: null, errMsg: 'Error: starting the apple pay session' });
        }
    };

    const renderButton = () => (
        <button id='applePayExpressCheckout' ref={applePayButtonRef} className={classNames(styles['apple-pay-button'], styles['apple-pay-button-white'], styles.applePayexpressCheckoutButton)} onClick={handleApplePay} disabled={!applePayInstance}></button>
    );

    const component = () => {
        const footerLink = LOCALE_UPDATED_SALE_LINK.includes(config('locale')) ? translate('footer.terms_of_use_link') : translate('footer.terms_of_sale_link');
        return (
            <form className={classNames(styles.applePay, !show ? styles.hidden : '')}>
                <div className={styles.applePayInnerBoxText}>Continuing will take you to Apple Pay.</div>
                <div className={styles.button}>
                    <button className={classNames(styles['apple-pay-button'], styles['apple-pay-button-black'], styles.applePayButton)} onClick={handleApplePay}></button>
                    <div className={styles['order-disclaimer']}>
                        {translate('checout_payment.disclaimer_text_part1')}
                        <a target="_blank" rel="noreferrer" href={footerLink}>
                            &nbsp;<u>{translate('footer.terms_of_sale')}</u>
                        </a>
                        .
                    </div>
                </div>
            </form>
        );
    };

    const selected = show ? 'apple-pay-checked' : '';
    return (
        <>
            {isExpressCheckout ? (
                renderButton()
            ) : (
                <div className={classNames(styles.applePayLayout, styles[`${selected}`])}>
                    <div className={styles.icons}>
                        <div className={styles['tick-icon']}>
                            <div className={classNames(styles['circular-radio'], styles['paynow-tick'], styles['img-tick'])}></div>
                        </div>
                        <div className={styles.braintreeApplePayLayoutTitle}>
                            {Icon && <div className={styles.icon}><Icon /></div>}
                            <div className={styles.layoutText}>Apple Pay</div>
                        </div>
                        {show && (
                            <div className={classNames(styles.amount, 'col-sm-2 col-xs-3')} id="adyen_total_price">
                                {currencyFormatter(cart.cartTotalWithShipping, cart.fractionDigits)}
                            </div>
                        )}
                    </div>
                    {component()}
                </div>
            )}
        </>
    );
};
